import { ClaimReservationsServiceAPI } from '@API/data-services/claim-reservations-service'
import { Modal } from '@Components/common/modal'
import { convertDateToIsoDate } from '@Components/util/utils'
import {
  AdminClaimReservationRequest,
  ClaimReservationCommunicationMethod,
} from '@Generated/back-office-client/src'
import { Button, Flex, Select, Text, TextField } from '@radix-ui/themes'
import { useMutation, useQuery } from '@tanstack/react-query'
import { cloneDeep } from 'lodash'

import { Form } from 'radix-ui'
import { useCallback, useState } from 'react'
import { formatPhoneNumber } from './phone-utils'

type AppointmentFormValidation = {
  nameValid: boolean
  dateValid: boolean
  numberOfTicketsValid: boolean
  timeSlotValid: boolean
}

const defaultValidationState: AppointmentFormValidation = {
  nameValid: true,
  dateValid: true,
  numberOfTicketsValid: true,
  timeSlotValid: true,
}

const ONE_MINUTE = 60 * 1000

export const ScheduleAppointment = ({
  onOpenChange,
  claimCenterId,
  refetchGrid,
  defaultDate,
}: {
  onOpenChange: (open: boolean) => void
  refetchGrid: () => void
  claimCenterId: string
  defaultDate: Date
}) => {
  const [validation, setValidation] = useState<AppointmentFormValidation>(defaultValidationState)
  const [selectedNumberOfTickets, setSelectedNumberOfTickets] = useState<number>()
  const [selectedDate, setSelectedDate] = useState<string>(convertDateToIsoDate(defaultDate))
  const [selectedTimeslot, setSelectedTimeslot] = useState<string>()
  const [phoneNumber, setPhoneNumber] = useState<string>()

  const ticketOptions = Array.from({ length: 14 }, (_, i) => {
    return (
      <Select.Item key={i} value={(i + 1).toString()}>
        {i + 1}
      </Select.Item>
    )
  })

  const { data: availability, isLoading: isAvailabilityLoading } = useQuery({
    queryKey: [`${claimCenterId}-${selectedDate}-${selectedNumberOfTickets}`],
    staleTime: ONE_MINUTE,
    queryFn: async () => {
      return await ClaimReservationsServiceAPI.getClaimCentersAvailability(
        selectedDate,
        claimCenterId,
        selectedNumberOfTickets!,
      )
    },
    enabled: !!selectedNumberOfTickets,
  })

  const {
    isPending: reservationPending,
    isError: reservationError,
    mutate: createReservation,
  } = useMutation({
    mutationFn: (adminClaimReservationRequest: AdminClaimReservationRequest) => {
      return ClaimReservationsServiceAPI.createReservation(
        claimCenterId,
        adminClaimReservationRequest,
      )
    },
    onSuccess: () => {
      onOpenChange(false)
      refetchGrid()
    },
  })

  const availabilityOptions = useCallback(() => {
    return availability?.flatMap((a) => {
      return a.timeslots.map((t) => {
        return (
          <Select.Item key={t.id} value={t.id}>
            {t.display.localTime}
          </Select.Item>
        )
      })
    })
  }, [availability])

  const hasAvailability = availability && availabilityOptions()?.length

  return (
    <Modal
      onOpenChange={onOpenChange}
      title="Schedule Appointment"
      description="Schedule appointment for a date and time"
      hideTitle={false}
      className="schedule-unavailability-dialog">
      <Form.Root
        onSubmit={(e) => {
          e.preventDefault()

          const formData = new FormData(e.currentTarget).entries()
          const data = Object.fromEntries(formData)
          const validation: AppointmentFormValidation = cloneDeep(defaultValidationState)

          if (!(data.name as string)) {
            validation.nameValid = false
          }

          if (!selectedDate) {
            validation.dateValid = false
          }

          if (!selectedNumberOfTickets) {
            validation.numberOfTicketsValid = false
          }

          if (!(validation.nameValid && validation.dateValid && validation.numberOfTicketsValid)) {
            setValidation(validation)
            return
          }

          createReservation({
            notes: null,
            numberOfTickets: selectedNumberOfTickets!,
            phone: phoneNumber ?? null,
            email: data.email ? (data.email as string) : null,
            name: data.name as string,
            timeslotId: selectedTimeslot!,
            communicationPreference: phoneNumber // if they have a phone number supplied, use that as the preference - otherwise use email if it exists. Default to phone but will be ignored in this scenario
              ? ClaimReservationCommunicationMethod.PHONE
              : data.email
                ? ClaimReservationCommunicationMethod.EMAIL
                : ClaimReservationCommunicationMethod.PHONE,
          })
        }}>
        <Flex gap="3" direction="column">
          <Form.Field name="name" className="form-field">
            <Form.Label>Name</Form.Label>
            <Form.Control asChild>
              <TextField.Root />
            </Form.Control>
          </Form.Field>

          <Form.Field name="phone" className="form-field">
            <Form.Label>Phone (optional)</Form.Label>
            <Form.Control asChild>
              <TextField.Root
                type="tel"
                value={phoneNumber ?? ''}
                onChange={(e) => {
                  setPhoneNumber(formatPhoneNumber(e.target.value))
                }}
              />
            </Form.Control>
          </Form.Field>

          <Form.Field name="email" className="form-field">
            <Form.Label>Email (optional)</Form.Label>
            <Form.Control asChild>
              <TextField.Root />
            </Form.Control>
          </Form.Field>

          <Form.Field name="numberOfTickets" className="form-field">
            <Form.Label>Select Number of Tickets</Form.Label>
            <Form.Control asChild>
              <Select.Root onValueChange={(value) => setSelectedNumberOfTickets(parseFloat(value))}>
                <Select.Trigger />
                <Select.Content>
                  {ticketOptions}
                  <Select.Item value="15">15+</Select.Item>
                </Select.Content>
              </Select.Root>
            </Form.Control>
          </Form.Field>

          <Flex justify={'between'} gap={'8'}>
            <Form.Field name="date" className="form-field half-width">
              <Form.Label>Select Date</Form.Label>
              <Form.Control style={{ display: 'flex' }} asChild>
                <input
                  type="date"
                  className="rt-reset rt-SelectTrigger rt-r-size-2 rt-variant-surface"
                  defaultValue={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                />
              </Form.Control>
            </Form.Field>

            {hasAvailability ? (
              <Form.Field name="timeslot" className="form-field half-width">
                <Form.Label>Select Timeslot</Form.Label>
                <Form.Control asChild>
                  <Select.Root onValueChange={setSelectedTimeslot}>
                    <Select.Trigger />
                    <Select.Content>{availabilityOptions()}</Select.Content>
                  </Select.Root>
                </Form.Control>
              </Form.Field>
            ) : null}

            {isAvailabilityLoading ? (
              <Flex align={'center'}>
                <Text>Loading...</Text>
              </Flex>
            ) : null}
          </Flex>

          {availability !== undefined && !hasAvailability ? (
            <Flex align="center">
              <Text>No availability for selected date and number of tickets</Text>
            </Flex>
          ) : null}
        </Flex>

        <Flex gap="3" justify="center">
          <ul className="schedule-validation">
            {validation.nameValid ? null : <li>You must enter a valid name</li>}
            {validation.dateValid ? null : <li>You must select a date</li>}
            {validation.numberOfTicketsValid ? null : <li>You must select an amount of tickets</li>}
            {validation.numberOfTicketsValid ? null : <li>You must select a time slot</li>}
          </ul>
        </Flex>

        {reservationError ? (
          <Text color="red">
            There was an error creating your reservation. Please try a new time slot
          </Text>
        ) : null}

        <Flex gap="3" justify="center" className="schedule-unavailability-dialog-controls">
          <Form.Submit asChild>
            {reservationPending ? (
              <Text>Creating Reservation...</Text>
            ) : (
              <Button disabled={!hasAvailability}>Submit</Button>
            )}
          </Form.Submit>
        </Flex>
      </Form.Root>
    </Modal>
  )
}
